import React, { useEffect, useMemo, useState } from 'react';
import BBALoader from '../BBALoader';
import { X, Paperclip, FileText, Minus, Plus } from 'lucide-react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  createTaskTimeRequest,
  respondToTaskTimeRequest,
  formatTaskTimeHours,
} from '../../services/taskTimeService';
import {
  TASK_TIME_STATUS,
  TASK_TIME_DEPARTMENTS,
  taskTimeDepartmentLabel,
  normalizeTaskTimeDepartment,
  taskTimeRequestHours,
} from '../../utils/taskTimeConstants';
import TaskTimeStatusBadge from '../taskTime/TaskTimeStatusBadge';

const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

function formatDate(value) {
  if (!value) return '—';
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed)
    ? new Date(parsed).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
}

/** 0.5 → "30 min", 1 → "1 hour", 1.5 → "1h 30m", 2 → "2 hours" */
function formatDuration(hours) {
  const h = Number(hours) || 0;
  if (h === 0.5) return '30 min';
  if (h % 1 === 0) return `${h} hour${h === 1 ? '' : 's'}`;
  return `${Math.floor(h)}h 30m`;
}

/**
 * Pooled Task Time modal scoped to one department page (or account-wide when
 * `department` is omitted, e.g. from the portal Tasks page).
 * `taskTime` is the result of useTaskTime() owned by the parent so the
 * KPI tile and modal share one balance.
 */
const DepartmentTaskTimeModal = ({ isOpen, onClose, department, taskTime, initialTab = 'tasks' }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const hasDepartment = !!department;
  // Opened to request work (Schedule Task / New Task → Implementation Task): the
  // form is the whole modal — no tabs, no task list. Managing existing requests
  // lives on the portal Tasks page.
  const requestOnly = initialTab === 'new';
  const [tab, setTab] = useState(initialTab);
  const [deptFilter, setDeptFilter] = useState(hasDepartment ? 'department' : 'all');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [newDepartment, setNewDepartment] = useState(normalizeTaskTimeDepartment(department));
  const [newHours, setNewHours] = useState(0.5);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [respondingId, setRespondingId] = useState(null);

  const departmentKey = normalizeTaskTimeDepartment(department);
  const departmentLabel = taskTimeDepartmentLabel(department);

  const {
    accountId,
    requests = [],
    allowanceHours,
    remainingHours,
    deductedHours,
    usedCallHours,
    periodEnd,
    loading,
    refresh,
  } = taskTime || {};

  const visibleRequests = useMemo(() => {
    if (deptFilter === 'all') return requests;
    return requests.filter((r) => r.department === departmentKey);
  }, [requests, deptFilter, departmentKey]);

  // Cap the request at what's left in the pool, in 30-min steps (8h absolute max).
  const maxRequestHours = useMemo(() => {
    if (remainingHours == null) return 8;
    return Math.min(8, Math.floor((Number(remainingHours) + 1e-9) / 0.5) * 0.5);
  }, [remainingHours]);
  const noHoursLeft = maxRequestHours < 0.5;

  useEffect(() => {
    setNewHours((h) => Math.min(Math.max(0.5, h), Math.max(0.5, maxRequestHours)));
  }, [maxRequestHours]);

  const stepHours = (delta) => {
    setNewHours((h) => Math.min(Math.max(0.5, h + delta), Math.max(0.5, maxRequestHours)));
  };

  if (!isOpen) return null;

  const userName =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Client';

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (attachments.length >= MAX_ATTACHMENTS) {
      setMessage(`Failed to attach: maximum of ${MAX_ATTACHMENTS} files per request.`);
      return;
    }
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      setMessage('Failed to attach: use images, PDF, Word, Excel, CSV, or text files.');
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setMessage('Failed to attach: files must be under 10MB.');
      return;
    }
    setUploadingFile(true);
    setMessage('');
    try {
      const path = `task-time-attachments/${accountId || 'unknown'}/${Date.now()}-${file.name}`;
      const uploadResult = await uploadBytes(storageRef(storage, path), file);
      const url = await getDownloadURL(uploadResult.ref);
      setAttachments((prev) => [...prev, { name: file.name, size: file.size, type: file.type, url }]);
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setMessage('Failed to upload attachment. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  const removeAttachment = (url) => {
    setAttachments((prev) => prev.filter((f) => f.url !== url));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert('You must be logged in to submit a task request.');
      return;
    }
    if (uploadingFile) {
      setMessage('Please wait for the attachment to finish uploading.');
      return;
    }
    if (noHoursLeft) {
      setMessage('Not enough hours remaining this period to request a task.');
      return;
    }
    setSubmitting(true);
    setMessage('');
    try {
      await createTaskTimeRequest({
        accountId,
        department: newDepartment,
        title,
        description,
        hours: newHours,
        attachments,
        requestedByName: userName,
      });
      setMessage('Request submitted. Your team will review and approve it.');
      setTitle('');
      setDescription('');
      setNewHours(0.5);
      setAttachments([]);
      // Request-only mode has no task list to fall back to — stay on the form
      // with the confirmation message.
      if (!requestOnly) setTab('tasks');
      await refresh?.();
    } catch (err) {
      console.error(err);
      setMessage('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespond = async (request, approved) => {
    if (!currentUser) return;
    setRespondingId(request.id);
    setMessage('');
    try {
      await respondToTaskTimeRequest(request.id, { approved });
      await refresh?.();
    } catch (err) {
      console.error(err);
      setMessage(err.message || 'Failed to update request.');
    } finally {
      setRespondingId(null);
    }
  };

  const pillClass = (active) =>
    `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
      active
        ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-800'
        : darkMode
          ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`;

  const balancePct =
    allowanceHours > 0
      ? Math.min(100, Math.round((((deductedHours || 0) + (usedCallHours || 0)) / allowanceHours) * 100))
      : 0;

  const resetDisplay = periodEnd
    ? new Date(periodEnd.getTime ? periodEnd.getTime() - 1 : periodEnd).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col`}
      >
        <div className={`relative p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={onClose}
            className={`absolute right-3 top-3 p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className={`text-lg font-semibold text-center px-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Implementation Hours
          </h2>
          {allowanceHours != null && (
            <div className="mt-2">
              <div className="flex justify-between text-xs mb-1">
                <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {formatTaskTimeHours(remainingHours)} of {formatTaskTimeHours(allowanceHours)} remaining this period
                </span>
                {resetDisplay && (
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Resets {resetDisplay}</span>
                )}
              </div>
              <div className="h-1.5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                <div
                  className={`h-full ${balancePct >= 100 ? 'bg-red-500' : 'bg-emerald-500'}`}
                  style={{ width: `${balancePct}%` }}
                />
              </div>
              <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Calls and tasks share one pool · task hours deduct once the task is approved
              </p>
            </div>
          )}
        </div>

        {!requestOnly && (
          <div className="px-4 pt-3 flex flex-wrap items-center gap-2">
            <button type="button" className={pillClass(tab === 'tasks')} onClick={() => setTab('tasks')}>
              My tasks
            </button>
            <button type="button" className={pillClass(tab === 'new')} onClick={() => setTab('new')}>
              New request
            </button>
            {tab === 'tasks' && hasDepartment && (
              <div className="ml-auto flex gap-1.5">
                <button
                  type="button"
                  className={pillClass(deptFilter === 'department')}
                  onClick={() => setDeptFilter('department')}
                >
                  {departmentLabel}
                </button>
                <button
                  type="button"
                  className={pillClass(deptFilter === 'all')}
                  onClick={() => setDeptFilter('all')}
                >
                  All
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {message && (
            <p
              className={`text-sm mb-3 ${
                message.includes('Failed') || message.includes('Not enough')
                  ? 'text-red-600'
                  : 'text-green-600'
              }`}
            >
              {message}
            </p>
          )}

          {tab === 'tasks' ? (
            loading ? (
              <div className="flex justify-center py-8">
                <BBALoader size={28} />
              </div>
            ) : visibleRequests.length === 0 ? (
              <p className={`text-sm text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No task requests yet. Submit a new request to get started.
              </p>
            ) : (
              <ul className="space-y-3">
                {visibleRequests.map((req) => {
                  const reqHours = taskTimeRequestHours(req) || 0;
                  // The client only signs off on tasks the BBA team proposed;
                  // their own requests wait on the team.
                  const awaitingClient =
                    req.status === TASK_TIME_STATUS.REQUESTED && req.requestedSource === 'crm';
                  const overBalance =
                    remainingHours != null && awaitingClient && reqHours > remainingHours + 1e-9;
                  const afterApproval =
                    remainingHours != null
                      ? Math.max(0, Math.round((remainingHours - reqHours) * 100) / 100)
                      : null;
                  return (
                    <li
                      key={req.id}
                      className={`p-3 rounded-lg border text-sm ${darkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex justify-between gap-2 items-start">
                        <div className="min-w-0">
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {req.title}
                          </p>
                          {req.description && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {req.description}
                            </p>
                          )}
                        </div>
                        <TaskTimeStatusBadge status={req.status} />
                      </div>

                      <div className={`flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="font-medium">{taskTimeDepartmentLabel(req.department)}</span>
                        <span>Requested {formatDate(req.requestedAt)}</span>
                        {reqHours > 0 && <span>{formatTaskTimeHours(reqHours)}</span>}
                      </div>

                      {Array.isArray(req.attachments) && req.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {req.attachments.map((f) => (
                            <a
                              key={f.url}
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                                darkMode
                                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                  : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              <Paperclip className="w-3 h-3" />
                              <span className="max-w-[140px] truncate">{f.name || 'Attachment'}</span>
                            </a>
                          ))}
                        </div>
                      )}

                      {req.crmNotes && (
                        <p className={`text-xs mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Note: {req.crmNotes}
                        </p>
                      )}

                      {awaitingClient && (
                        <div className="mt-3">
                          {overBalance ? (
                            <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                              Not enough task time remaining ({formatTaskTimeHours(remainingHours)} left).
                              Contact your team about adding hours.
                            </p>
                          ) : (
                            afterApproval != null && (
                              <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Approving uses {formatTaskTimeHours(reqHours)} —{' '}
                                {formatTaskTimeHours(afterApproval)} left after.
                              </p>
                            )
                          )}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={respondingId === req.id || overBalance}
                              onClick={() => handleRespond(req, true)}
                              className="flex-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-xs font-medium"
                            >
                              Approve {formatTaskTimeHours(reqHours)}
                            </button>
                            <button
                              type="button"
                              disabled={respondingId === req.id}
                              onClick={() => handleRespond(req, false)}
                              className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 rounded-lg text-xs font-medium"
                            >
                              Deny
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Task title
                </label>
                <input
                  type="text"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What do you need help with?"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Department
                </label>
                <select
                  value={newDepartment}
                  onChange={(e) => setNewDepartment(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  {TASK_TIME_DEPARTMENTS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Time needed
                </label>
                {noHoursLeft ? (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    No hours remaining this period. Contact your team about adding hours.
                  </p>
                ) : (
                  <div
                    className={`flex items-center justify-between rounded-lg border px-2 py-1.5 ${
                      darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => stepHours(-0.5)}
                      disabled={newHours <= 0.5}
                      aria-label="30 minutes less"
                      className={`p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className={`text-sm font-semibold tabular-nums ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatDuration(newHours)}
                    </span>
                    <button
                      type="button"
                      onClick={() => stepHours(0.5)}
                      disabled={newHours >= maxRequestHours}
                      aria-label="30 minutes more"
                      className={`p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {!noHoursLeft && (
                  <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {remainingHours != null
                      ? `Up to ${formatDuration(maxRequestHours)} — you have ${formatTaskTimeHours(remainingHours)} left this period.`
                      : 'In 30-minute increments.'}{' '}
                    Your team reviews and approves the task before hours deduct.
                  </p>
                )}
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description
                </label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the task in detail…"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Attachments
                </label>
                {attachments.length > 0 && (
                  <ul className="space-y-1.5 mb-2">
                    {attachments.map((f) => (
                      <li
                        key={f.url}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs ${
                          darkMode ? 'border-gray-600 bg-gray-700/50 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-700'
                        }`}
                      >
                        <FileText className="w-3.5 h-3.5 shrink-0 text-red-500" />
                        <span className="flex-1 min-w-0 truncate">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(f.url)}
                          className={`p-0.5 rounded ${darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <label
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
                    uploadingFile || attachments.length >= MAX_ATTACHMENTS
                      ? 'opacity-60 cursor-not-allowed border-gray-300 dark:border-gray-600 text-gray-400'
                      : darkMode
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Paperclip className="w-4 h-4" />
                  {uploadingFile ? 'Uploading…' : 'Attach a file'}
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={uploadingFile || attachments.length >= MAX_ATTACHMENTS}
                    accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
                  />
                </label>
                <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Up to {MAX_ATTACHMENTS} files, 10MB each — images, PDF, Word, Excel, CSV, or text.
                </p>
              </div>
              <button
                type="submit"
                disabled={submitting || uploadingFile || noHoursLeft}
                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
              >
                {submitting ? 'Submitting…' : noHoursLeft ? 'No Hours Remaining' : 'Submit Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepartmentTaskTimeModal;
